/**
 * DailyLog (SK-LOG, T-17, DS-1g-04 §4.4) — AC-1g-15.
 *
 * Per-device browsing fallback over the FULL local capture store (the
 * caller supplies whatever `getAllOutboxRecords()` returns — DailyLog has
 * no DB-read opinion of its own, matching DecisionLogView/
 * MasterActionListView's convention; this is NOT a server listing, §4.9).
 *
 * §7.M4 (Divergence-Memo reconciliation): a browsing LIST only — a "Daily
 * logs" title + New-capture "+", filter pills, and rows bucketed under date
 * dividers (newest day first). The whole row is a tap target that opens the
 * shared per-item review/edit surface (`LogEntryReview`, reached app-level and
 * keyed by `capture_id` so the Reports deep-link (M7) reaches the SAME screen)
 * — this component itself no longer edits/finalizes a row; it emits
 * `onOpenEntry(capture_id)` and lets the app mount the review surface. The
 * find/edit/re-route/delete semantics (pre-wire undoToDraft; REJECTED
 * edit&re-save; DELIVERED supersede) now live in `LogEntryReview`.
 *
 * The supersede-carrier emitters below (`submitCorrection`/`submitRemoval`)
 * and the local hard-delete (`deleteLocalRecord`) stay EXPORTED from here —
 * `LogEntryReview` imports them, and their §4.6 / AC-1g-07 contracts are
 * covered by this file's unit tests.
 *
 * Lock 2 (T-16, AC-1g-28): carrier records (`!isTriageable`) are excluded
 * from the row list entirely and rendered ONLY as an audit line attached
 * to their canonical (matched by `payload.supersedes_ref`) — no finalize/
 * discard affordance exists for them here, or anywhere else.
 *
 * Imposes no review step (AC-1g-15): every affordance here is optional;
 * nothing about opening, ignoring, or leaving this screen blocks capture
 * or delivery — there is no "must review before continuing" gate anywhere
 * in this file.
 */

import { useMemo, useState } from "preact/hooks";
import { openOutboxDB } from "../outbox/db";
import { saveToProject, inheritedProjectRef, PROJECT_REF_KEY } from "../outbox/state-machine";
import { mintCaptureId, mintClientId } from "../outbox/idempotency";
import { buildCarrierFields } from "../triage/ordinals";
import { isTriageable, markCarrierPayload } from "../triage/carrier-filter";
import {
  attributionOf, criticalityOf, decidesOf, isDecision, isToday,
  isUnfinishedCapture, payloadStr, photoCount, roomOf, timeLabel, titleOf,
} from "./entry-meta";
import { CaptureThumbs } from "./CaptureThumbs";
import { canonicalRefOf, foldLogRecords } from "../read/log-fold";
import type { OutboxRecord } from "../outbox/types";
import { matchesTaggedReadFilter, presentPhases, presentTrades } from "../read/tag-filter";

export interface DailyLogProps {
  /** The full device-local capture store snapshot (`getAllOutboxRecords()`)
   * — DRAFT rows (not yet saved) are filtered out here; every other state
   * is a candidate row, subject to Lock 2 below. */
  records: OutboxRecord[];
  /** §7.M4: whole-row tap → the shared per-item review/edit surface, keyed by
   * `capture_id` (the app mounts `LogEntryReview`). */
  onOpenEntry?: (capture_id: string) => void;
  /** §7.M4: the header "+" — a fresh capture always starts on the Record
   * tab (never an inline add on this screen). */
  onNewCapture?: () => void;
  /** §align: the selected project's visible name (Fray/Foster) — rendered as
   * the header subtitle per the spec (`· {projectLabel}`). */
  projectLabel?: string;
  /** Server-folded entries this device has NO outbox record for, adapted to the
   * row shape (`serverLogEntryAsRecord`). Present after a factory reset, on a
   * second phone, or for a principal reading a project they did not capture —
   * without them the log would be empty while the server plainly holds it.
   *
   * READ-ONLY: no local blobs (so no thumbnail) and no tap target, because the
   * edit path needs a real outbox record to supersede. Interleaved into the same
   * date groups so a restored log reads as one list, not two. */
  serverOnlyRecords?: OutboxRecord[];
}

/** The ONE local hard-delete primitive — legal ONLY for pre-wire and REJECTED
 * rows (nothing was ever committed for them); post-finalize rows are never
 * deleted, only superseded. Reuses db.ts's already-exported `openOutboxDB()`
 * handle directly (the reuse pattern T-15's ordinals.ts established) rather
 * than editing db.ts to add a dedicated delete export. `LogEntryReview` is the
 * caller. */
export async function deleteLocalRecord(capture_id: string): Promise<void> {
  const db = await openOutboxDB();
  await db.delete("outbox", capture_id);
}

/** Post-finalize correct/re-route (§4.6 supersede path): a fresh carrier
 * outbox group referencing the canonical via `supersedes_ref` +
 * `change_ordinal`, carrying a human-typed correction note. `parts: []`
 * — a correction is text-only; it does not restate the canonical's media.
 * `highestInCollapsedRead` is passed as 0 (Stage A: LIVE read transport is
 * gated per PIN-1g→1l-PWA-TRANSPORT/S8, so the collapsed-read half of the
 * §4.6 allocation formula is not available yet here; the device-cursor
 * half still holds strictly-monotonic-per-canonical on its own, per
 * ordinals.ts's own module doc). */
export async function submitCorrection(
  canonical: OutboxRecord,
  note: string,
  /** The highest `change_ordinal` the SERVER reports already spent on this
   * entry (`GET /mark/logs` → `change_ordinal`), or 0 when unknown.
   *
   * The device keeps its own per-canonical cursor, which is the true high-water
   * mark only while the capturing device is the only one that ever corrects the
   * entry. A restored or second device starts that cursor at zero and would
   * allocate at or below an ordinal already spent — its correction would land
   * in `ops.capture`, be perfectly valid, and silently LOSE the INV-4
   * comparison to the older one. `buildCarrierFields` takes the max of the two. */
  /** §4.3 re-open-from-log: the fields the human actually retyped on the save
   * screen. Carried on the carrier so the correction changes what the entry
   * MEANS, not just what an audit line says about it — `transcript` is where the
   * goalkeeper already reads a capture's content (#35b), so a corrected entry
   * reads correctly downstream too. Additive to the existing payload; a caller
   * that only wants to leave a note (the old behaviour) passes nothing here. */
  edits?: Record<string, unknown>,
  /** The highest `change_ordinal` the SERVER reports already spent on this
   * entry (`GET /mark/logs` → `change_ordinal`), or 0 when unknown.
   *
   * The device keeps its own per-canonical cursor, which is the true high-water
   * mark only while the capturing device is the only one that ever corrects the
   * entry. A restored or second device starts that cursor at zero and would
   * allocate at or below an ordinal already spent — its correction would land in
   * `ops.capture`, be perfectly valid, and silently LOSE the INV-4 comparison to
   * the older one. `buildCarrierFields` takes the max of the two. */
  serverHighestOrdinal = 0,
): Promise<OutboxRecord> {
  // AC-1g-07 (TW-1g-PROJECT-INFER): a correction is a fresh emission scoped
  // to the SAME project as the canonical it supersedes — inherit project_ref
  // verbatim, never re-select/infer. Fail-closed if the canonical lost it.
  const project_ref = inheritedProjectRef(canonical);
  const carrierFields = await buildCarrierFields(canonical.capture_id, serverHighestOrdinal);
  const route = edits?.route as { skill?: unknown; op?: unknown } | undefined;
  const addsFanout =
    (route?.skill === "master_action_list" && route.op === "upsert") ||
    (route?.skill === "decision_log_dayone" && route.op === "raise");
  const source_refs = addsFanout
    ? Array.from(new Set([
        canonical.capture_id,
        ...canonical.envelope.source_refs,
        ...canonical.envelope.media
          .filter((part) => part.role === "primary_photo" && part.media_ref.length > 0)
          .map((part) => part.media_ref),
      ]))
    : canonical.envelope.source_refs;
  return saveToProject({
    capture_id: mintCaptureId(),
    client_id: mintClientId(),
    tenant_id: canonical.envelope.tenant_id,
    skill: canonical.envelope.skill,
    skill_version: canonical.envelope.skill_version,
    // Provenance only: no bytes are resent. The source id and resolved photo
    // keys let the fan-out row render the image that belongs to the Daily Log.
    source_refs,
    parts: [],
    payload: markCarrierPayload({
      [PROJECT_REF_KEY]: project_ref,
      ...carrierFields,
      // The note stays OPTIONAL. Design §4.3 is explicit that the capture path
      // carries "zero added confirmation dialogs" and rule #5 makes nothing
      // mandatory at Save but integrity gates, so fixing a typo must not require
      // the human to justify it in writing. Omitted entirely when blank rather
      // than written as "" — an empty audit line reads as a missing one.
      ...(note.trim() ? { correction_note: note.trim() } : {}),
      ...(edits ?? {}),
    }),
  });
}

/** Post-finalize delete (design §4.3: "a status change to a terminal
 * status via the same path, never a row deletion") — the same supersede
 * shape as `submitCorrection`, carrying a `status_change: "removed"`
 * marker instead of free text. */
export async function submitRemoval(
  canonical: OutboxRecord,
  /** See `submitCorrection` — same reason, same failure if it is wrong. */
  serverHighestOrdinal = 0,
): Promise<OutboxRecord> {
  // AC-1g-07 (TW-1g-PROJECT-INFER): a removal is a fresh emission scoped to
  // the SAME project as the canonical it supersedes — inherit project_ref
  // verbatim, never re-select/infer. Fail-closed if the canonical lost it.
  const project_ref = inheritedProjectRef(canonical);
  const carrierFields = await buildCarrierFields(canonical.capture_id, serverHighestOrdinal);
  return saveToProject({
    capture_id: mintCaptureId(),
    client_id: mintClientId(),
    tenant_id: canonical.envelope.tenant_id,
    skill: canonical.envelope.skill,
    skill_version: canonical.envelope.skill_version,
    source_refs: canonical.envelope.source_refs,
    parts: [],
    payload: markCarrierPayload({ [PROJECT_REF_KEY]: project_ref, ...carrierFields, status_change: "removed" }),
  });
}

export type LogFilter = "all" | "today" | "critical" | "decisions";

function matchesFilter(record: OutboxRecord, filter: LogFilter): boolean {
  switch (filter) {
    case "today": return isToday(record);
    case "critical": return criticalityOf(record) === "high";
    case "decisions": return isDecision(record);
    default: return true;
  }
}

const LOG_FILTERS: { id: LogFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "today", label: "Today" },
  { id: "critical", label: "Critical" },
  { id: "decisions", label: "Decisions" },
];

// §7.M4: the divider label — "Weekday, Month D, YYYY" (e.g. "Wednesday,
// July 22, 2026"), matching the preview's DATE_LABELS format.
const DAY_FMT = new Intl.DateTimeFormat("en-US", {
  weekday: "long",
  month: "long",
  day: "numeric",
  year: "numeric",
});
/** A stable YYYY-MM-DD bucket key off the record's local capture day. */
function dayKeyOf(record: OutboxRecord): string {
  const d = new Date(record.createdAt);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function DailyLog({ records, onOpenEntry, onNewCapture, projectLabel, serverOnlyRecords }: DailyLogProps) {
  const [filter, setFilter] = useState<LogFilter>("all");
  const [phase, setPhase] = useState("all");
  const [trade, setTrade] = useState("all");

  // Lock 2 (AC-1g-28): carrier entries never appear as list rows — AND, since
  // 2026-07-27, their corrections are folded onto the canonical they supersede
  // so an edit is actually VISIBLE here. Before the fold, a post-finalize
  // correction committed a perfectly good carrier that no surface could ever
  // show, so the log kept displaying the old text forever. Display only: the
  // stored records are untouched and the per-row audit trail below still lists
  // every carrier (§4.11 — the original stays retrievable).
  const foldedCanonical = useMemo(
    () => foldLogRecords(records).canonicals,
    [records],
  );
  // Server-only rows join the canonical set for display. They are already
  // folded server-side (corrections applied, removals withheld), so they skip
  // the local fold entirely.
  const serverRows = serverOnlyRecords ?? [];
  const allCanonical = [
    ...foldedCanonical.filter((r) => r.state !== "draft" && isTriageable(r)),
    ...serverRows,
  ];
  const taggedRows = allCanonical.map((record) => ({
    record,
    phase: payloadStr(record, "phase"),
    trade: payloadStr(record, "trade"),
  }));
  const phaseOptions = presentPhases(taggedRows);
  const tradeOptions = presentTrades(taggedRows);
  const canonicalRows = taggedRows
    .filter(({ record }) => matchesFilter(record, filter))
    .filter((value) => matchesTaggedReadFilter(value, {
      ...(phase !== "all" ? { phase } : {}),
      ...(trade !== "all" ? { trade } : {}),
    }))
    .map(({ record }) => record);
  const carrierRows = records.filter((r) => !isTriageable(r));

  // §7.M4: bucket the rows into date-divider groups, newest day first
  // (the preview's `keys.sort().reverse()`).
  const groups = useMemo(() => {
    const byDay = new Map<string, OutboxRecord[]>();
    for (const r of canonicalRows) {
      const key = dayKeyOf(r);
      const bucket = byDay.get(key);
      if (bucket) bucket.push(r);
      else byDay.set(key, [r]);
    }
    return [...byDay.keys()]
      .sort()
      .reverse()
      .map((key) => ({
        key,
        label: DAY_FMT.format(new Date(`${key}T12:00:00`)),
        // Newest FIRST within the day, matching the day dividers above it.
        // The rows arrived in insertion order — this device's own outbox first
        // (oldest-first), then any server-only backfill appended — so the most
        // recent capture sat at the BOTTOM of today's group and a super who had
        // just recorded something had to scroll past the whole day to find it.
        // `createdAt` is the same field the day bucket is keyed on, so a row can
        // never sort into a day it is not displayed under.
        rows: [...byDay.get(key)!].sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        ),
      }));
  }, [canonicalRows]);

  function auditLinesFor(capture_id: string): OutboxRecord[] {
    return carrierRows.filter((c) => canonicalRefOf(c) === capture_id);
  }

  return (
    <section data-skin-slot="SK-LOG" data-testid="daily-log">
      <div
        class="log-header"
        data-testid="daily-log-header"
        style="display:flex;align-items:baseline;justify-content:space-between;gap:12px"
      >
        <div>
          <div class="log-title" style="font-size:20px;font-weight:700;color:var(--mark-text-primary)">
            Daily logs
          </div>
          {projectLabel && (
            <div style="font-size:11.5px;color:var(--mark-text-muted);margin-top:8px;line-height:1.4">{projectLabel}</div>
          )}
        </div>
        <button
          type="button"
          class="log-new-capture"
          data-testid="daily-log-new-capture"
          title="New capture"
          aria-label="New capture"
          onClick={() => onNewCapture?.()}
          style="min-height:44px;min-width:44px;background:none;border:none;color:var(--mark-accent-amber);font-size:20px;font-weight:700;cursor:pointer"
        >
          +
        </button>
      </div>

      <div class="log-filters" data-testid="daily-log-filters" role="tablist">
        {LOG_FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            class="log-filter-pill"
            data-testid={`daily-log-filter-${f.id}`}
            aria-pressed={filter === f.id}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>
      {tradeOptions.length > 0 && (
        <div class="log-filters" aria-label="Trade">
          <button type="button" class="log-filter-pill" data-testid="daily-log-trade-all" aria-pressed={trade === "all"} onClick={() => setTrade("all")}>All trades</button>
          {tradeOptions.map((value) => (
            <button key={value} type="button" class="log-filter-pill" data-testid={`daily-log-trade-${value}`} aria-pressed={trade === value} onClick={() => setTrade(value)}>{value}</button>
          ))}
        </div>
      )}
      {phaseOptions.length > 0 && (
        <div class="log-filters" aria-label="Phase">
          <button type="button" class="log-filter-pill" data-testid="daily-log-phase-all" aria-pressed={phase === "all"} onClick={() => setPhase("all")}>All phases</button>
          {phaseOptions.map((option) => (
            <button key={option.id} type="button" class="log-filter-pill" data-testid={`daily-log-phase-${option.id}`} aria-pressed={phase === option.id} onClick={() => setPhase(option.id)}>{option.label}</button>
          ))}
        </div>
      )}

      <div data-testid="daily-log-groups" style="display:flex;flex-direction:column">
        {groups.map((g) => (
          <div key={g.key} data-testid={`daily-log-day-${g.key}`}>
            <div class="log-date-divider" style="display:flex;align-items:center;gap:12px;padding:16px 0 4px">
              <span style="flex:1;height:1px;background:var(--mark-border-default)" />
              <span style="font-size:11px;font-weight:600;letter-spacing:0.8px;text-transform:uppercase;color:var(--mark-text-faint);white-space:nowrap">
                {g.label}
              </span>
              <span style="flex:1;height:1px;background:var(--mark-border-default)" />
            </div>
            <ul data-testid="daily-log-list">
              {g.rows.map((record) => {
                const id = record.capture_id;
                const audit = auditLinesFor(id);
                // #23a: a server-only row IS openable now. It used to be inert
                // because there was no outbox record to supersede — which meant a
                // restored device could read its own log and not fix a typo in
                // it. The record is synthesized with the read's scope and the
                // server's high-water ordinal, and `state: "delivered"` routes
                // the save to the supersede branch (never a local rewrite).
                // `data-server-only` stays: it still has no local blobs, and
                // anything that needs to know can still ask.
                const serverOnly = record.envelope.payload["_outbox_kind"] === "server";
                return (
                  <li
                    key={id}
                    data-testid={`daily-log-row-${id}`}
                    data-server-only={serverOnly ? "true" : undefined}
                    style="cursor:pointer"
                    onClick={() => onOpenEntry?.(id)}
                  >
                    <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:12px">
                      <span style="font-size:16px;font-weight:700;line-height:20px;color:var(--mark-text-primary)">{titleOf(record)}</span>
                      {/* An abandoned pre-persist still holds its audio, so it
                          stays visible — but it is not a finished entry and must
                          not read as one. The pill sits where "Critical" sits
                          because it is the same kind of thing: a status about
                          the row, not part of what the row says. */}
                      {isUnfinishedCapture(record) && (
                        <span
                          class="criticality-pill"
                          data-criticality="unfinished"
                          data-testid={`daily-log-row-unfinished-${id}`}
                        >
                          Unfinished
                        </span>
                      )}
                      {criticalityOf(record) === "high" && (
                        <span class="criticality-pill" data-criticality="high">
                          Critical
                        </span>
                      )}
                    </div>
                    <div class="log-card-meta" data-testid={`daily-log-row-meta-${id}`}>
                      {roomOf(record) && <span class="log-card-room">{roomOf(record)}</span>}
                      {decidesOf(record) && <span class="log-card-tag">Decision maker: {decidesOf(record)}</span>}
                      <span class="log-card-time">{timeLabel(record)}</span>
                      {photoCount(record) > 0 && (
                        <span class="log-card-photos" data-testid={`daily-log-row-photos-${id}`}>
                          {photoCount(record)} photo{photoCount(record) === 1 ? "" : "s"}
                        </span>
                      )}
                    </div>

                    {/* Who wrote this. Server rows only — a row this phone
                        captured would say "filed by me" on every line. Renders
                        nothing at all when unattributed, rather than a byline
                        with a blank in it. */}
                    {attributionOf(record) && (
                      <div class="log-card-meta" data-testid={`daily-log-row-by-${id}`}>
                        <span class="log-card-by">{attributionOf(record)}</span>
                      </div>
                    )}

                    {/* #39: real photo thumbnail(s) from the durable IDB blob
                        store (shared CaptureThumbs) — replaces the old count-based
                        grey placeholder tiles that never showed the actual image.
                        Self-gates to null when no local blob (seed/remote row);
                        the "N photos" count above remains the fallback signal. */}
                    {photoCount(record) > 0 && (
                      <CaptureThumbs
                        capture_id={record.capture_id}
                        size={56}
                        stripTestid={`daily-log-row-thumbs-${id}`}
                        itemTestid={`daily-log-row-thumb-${id}`}
                      />
                    )}

                    {audit.length > 0 && (
                      <ul data-testid={`daily-log-audit-${id}`}>
                        {audit.map((c) => (
                          <li key={c.capture_id} data-testid={`daily-log-audit-line-${c.capture_id}`}>
                            {/* A correction no longer REQUIRES a typed note
                                (design §4.3: zero added dialogs), so the old
                                fallback labelled every note-less carrier
                                "status changed" — which read as a status change
                                on an entry whose text had just been corrected.
                                Name what actually happened, and use the same
                                clock format as the row rather than a raw ISO
                                string (rule #10: no jargon in the field). */}
                            {c.envelope.payload["status_change"] === "removed"
                              ? "Deleted"
                              : typeof c.envelope.payload["correction_note"] === "string"
                                ? (c.envelope.payload["correction_note"] as string)
                                : "Edited"}{" "}
                            ({timeLabel(c)})
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}
